import * as React from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { OrganizationNotificationType } from 'ab-enums/organizationNotificationType.enum';

import * as ReduxFormKeys from 'af-constants/reduxForms';

import EmailCreate from 'af-components/EditableOrganizationEmailPill/EmailCreate';
import EmailPillList from 'af-components/EditableOrganizationEmailPill/EmailPillList';

import * as OrganizationContactActions from 'af-actions/organizationContact';

import { OrganizationContactFM } from './formModel';
import styles from './styles.module.scss';

interface EmailContact {
	id: number;
	email: string;
}

type OwnProps = {
	notificationType: OrganizationNotificationType;
};

type Props = ConnectedProps<typeof connector> & OwnProps;

const EmailList: React.FC<Props> = (props) => {

	const {
		findOrganizationContacts,
		createOrganizationContact,
		deleteOrganizationContact,
		updateOrganizationContact,
		notificationType,
	} = props;

	const [emails, setEmails] = React.useState<EmailContact[]>([]);

	const fetchOrganizationContacts = React.useCallback(async () => {
		const contacts = await findOrganizationContacts(notificationType);
		setEmails(contacts.emailContacts);
	}, [findOrganizationContacts, notificationType]);

	React.useEffect(() => {
		fetchOrganizationContacts();
	}, [fetchOrganizationContacts]);

	const createEmailContact = React.useCallback(async (form: OrganizationContactFM) => {
		await createOrganizationContact({ email: form.email, type: notificationType });
		await fetchOrganizationContacts();
	}, [createOrganizationContact, fetchOrganizationContacts, notificationType]);

	const deleteEmailContact = React.useCallback(async (id: number) => {
		await deleteOrganizationContact(id);
		await fetchOrganizationContacts();
	}, [deleteOrganizationContact, fetchOrganizationContacts]);

	const updateEmailContact = React.useCallback(async (id: number, form: OrganizationContactFM) => {
		await updateOrganizationContact(
			id,
			OrganizationContactFM.toUpdateRM(form)
		);
		await fetchOrganizationContacts();
	}, [fetchOrganizationContacts, updateOrganizationContact]);

	return (
		<>
			{!emails.length && <div className="m-t-m m-l-l">Add an email to start sending notifications</div>}
			<div className={styles['email-list']}>
				<EmailPillList
					deleteEmailContact={deleteEmailContact}
					emails={emails}
					formKey={ReduxFormKeys.COMPANIES_REVENUE_NOTIFICATION_CONTACT_UPDATE}
					updateEmailContact={updateEmailContact}
				/>
			</div>
			<EmailCreate createEmail={createEmailContact} form={ReduxFormKeys.COMPANIES_REVENUE_NOTIFICATION_CONTACT_CREATE} />
		</>
	);
};

function mapDispatchToProps() {
	return {
		findOrganizationContacts: OrganizationContactActions.findOrganizationContacts,
		createOrganizationContact: OrganizationContactActions.createOrganizationContact,
		deleteOrganizationContact: OrganizationContactActions.deleteOrganizationContact,
		updateOrganizationContact: OrganizationContactActions.updateOrganizationContact,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EmailList);
